import React, { useState } from 'react';
import { JourneyStopType, JourneyStop as JourneyStopV2 } from '../types/journey';
import { getCountryFlag } from '../shared/countryFlag';
import NightsStepper from './NightsStepper';

// Props for the JourneyStop component
// Accepts both v1 (legacy) and v2 stop formats
interface JourneyStopProps {
  stop: JourneyStopType | JourneyStopV2 | any;
  isFirst?: boolean;
  isNew?: boolean;
  /** Disable the nights stepper (e.g. while awaiting AI approval of an unsaved stop). */
  disableNights?: boolean;
}

/**
 * Helper to get location from stop in either v1 or v2 format.
 */
function getStopLocation(stop: any): { city: string; country: string } {
  // v2 format uses 'city' and 'country'
  if ('city' in stop) {
    return { city: stop.city, country: stop.country };
  }
  // v1 format uses 'location' and 'location_country'
  return { city: stop.location, country: stop.location_country };
}

/**
 * Helper to get activities from stop in either v1 or v2 format.
 */
function getActivities(stop: any): string[] {
  // v2 format uses 'activities'
  if (Array.isArray(stop.activities)) {
    return stop.activities;
  }
  // v1 format uses 'things_to_do'
  if (Array.isArray(stop.things_to_do)) {
    return stop.things_to_do;
  }
  return [];
}

/**
 * JourneyStop component
 * Displays a journey stop with location and things to do.
 * Supports both v1 (legacy) and v2 (new) stop formats.
 * Stop badges are now handled by the parent Journey component on the timeline.
 */
const JourneyStop: React.FC<JourneyStopProps> = ({ stop, isFirst = false, isNew = false, disableNights = false }) => {
  const [isThingsToDoExpanded, setIsThingsToDoExpanded] = useState(false);
  const location = getStopLocation(stop);
  const activities = getActivities(stop);
  const hasActivities = activities.length > 0;
  const flag = getCountryFlag(location.country);

  return (
    <div className="pl-8">
      <div className={`relative rounded-lg border p-4 ${isNew ? 'border-orange-400 bg-orange-50' : 'border-[var(--border)] bg-[var(--surface-alt)]'}`}>
        {/* Nights stepper tucked under the edit/remove buttons the parent
            floats over the card's top-right corner. */}
        {stop.id && (
          <div className="absolute top-11 right-3 z-10">
            <NightsStepper stopId={stop.id} initialHours={stop.stopDurationHours} disabled={disableNights} />
          </div>
        )}
        {/* Location header. Right padding reserves room for the stepper. */}
        <div className="mb-3 pr-32">
          <div className={`text-lg font-semibold ${isNew ? 'text-orange-600' : 'text-[var(--text)]'}`}>
            {/* Show the flag when we have one; otherwise keep the country name so
                no information is lost for unmapped countries. */}
            {location.city}
            {flag ? <span className="ml-2">{flag}</span> : `, ${location.country}`}
            {isFirst && <span className="text-[var(--text-muted)] text-sm ml-2 font-normal">(Start)</span>}
          </div>
        </div>

        {/* Things to do - Toggle visibility */}
        <div>
          <div className={`font-medium text-sm mb-2 flex items-center gap-2 ${isNew ? 'text-orange-600' : 'text-[var(--text-muted)]'}`}>
            <div className="flex items-center gap-1">
              Things to do
            </div>
            {hasActivities && (
              <button
                onClick={() => setIsThingsToDoExpanded(!isThingsToDoExpanded)}
                className={`px-2 py-0.5 text-xs font-medium rounded transition-colors duration-200 ${
                  isNew
                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    : 'bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--border)]'
                }`}
                style={{
                  minWidth: '48px',
                  minHeight: '20px',
                  fontSize: '12px',
                  touchAction: 'manipulation'
                }}
              >
                {isThingsToDoExpanded ? 'hide' : 'show'}
              </button>
            )}
          </div>
          {hasActivities && isThingsToDoExpanded && (
            <ul className={`text-sm leading-relaxed pl-4 ${isNew ? 'text-orange-600' : 'text-[var(--text)]'}`}>
              {activities.map((activity, index) => (
                <li key={index} className="mb-1">• {activity}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default JourneyStop; 